import {
  usePaginatedData,
} from "@/utils/vue_helpers";
import {
  stringNotEmpty,
  debounce,
  isOtpInvalid,
} from "@/utils/functions";
import { computed, reactive, ref, watch } from "vue";
import { searchDebounceTimeInMs, requestStatus, INVALID_OTP } from "@/utils/constants";
import apiCall from "@/utils/api";

const BASE_URL = "/api/client/scheme-plan-application";

const SCHEME_PLAN_APPLICATION_STAGE_LABELS = {
  "Scheme Plan Application Pre-vetting by Physical Planner": "Physical Planner Pre-vetting",
  "Scheme Plan Application Billing": "Billing",
  "Scheme Plan Application Approval by Physical Planner": "Physical Planner Approval",
  "Scheme Plan Application Approval by Director Physical Planning": "Director Physical Planning Approval",
};

export function formatSchemePlanApplicationStage(stage) {
  if (!stage) {
    return "-";
  }

  return SCHEME_PLAN_APPLICATION_STAGE_LABELS[stage] || stage;
}

/**
 * Verify that a PN, title deed PN, or LR number belongs to the client identified by id_number.
 */
export function useVerifyProperty() {
  const verifyStatus = ref(requestStatus.NOT_SENDING);
  const verifying = computed(() => verifyStatus.value === requestStatus.SENDING);
  const verifiedData = ref(null);
  const verifyError = ref(null);

  async function verifyProperty({ pn, idNumber }) {
    verifyStatus.value = requestStatus.SENDING;
    verifyError.value = null;
    verifiedData.value = null;
    try {
      const response = await apiCall({
        url: `${BASE_URL}/verify-property`,
        data: { pn, id_number: idNumber },
        method: "POST",
      });
      verifiedData.value = response;
      verifyStatus.value = requestStatus.COMPLETE;
      return response;
    } catch (error) {
      verifyStatus.value = requestStatus.ERROR;
      verifyError.value =
        error?.response?.data?.message || "Verification failed.";
      throw error;
    }
  }

  return { verifying, verifiedData, verifyError, verifyProperty };
}

/**
 * Send OTP for scheme plan application verification.
 */
export function useSendApplicationOtp() {
  const resendOtpTimeoutInSeconds = 90;

  const otpData = reactive({
    sendOtpRequestStatus: requestStatus.NOT_SENDING,
    otpSent: false,
    resendOtpTimeout: 0,
    otpModel: "",
  });

  let resendOtpTimeoutId = null;

  function updateResendOtpTimeout() {
    if (otpData.resendOtpTimeout > 0) {
      otpData.resendOtpTimeout -= 1;
      resendOtpTimeoutId = setTimeout(() => {
        updateResendOtpTimeout();
      }, 1000);
    }
  }

  function resetOtp() {
    clearTimeout(resendOtpTimeoutId);
    otpData.otpModel = "";
    otpData.otpSent = false;
    otpData.resendOtpTimeout = 0;
  }

  async function sendOtp({ idNumber }) {
    otpData.sendOtpRequestStatus = requestStatus.SENDING;
    try {
      otpData.resendOtpTimeout = resendOtpTimeoutInSeconds;
      updateResendOtpTimeout();
      await apiCall({
        url: `${BASE_URL}/send-otp`,
        data: { id_number: idNumber },
        method: "POST",
      });
      otpData.sendOtpRequestStatus = requestStatus.COMPLETE;
      otpData.otpSent = true;
    } catch (error) {
      otpData.sendOtpRequestStatus = requestStatus.ERROR;
    }
  }

  const sendingOtp = computed(
    () => otpData.sendOtpRequestStatus === requestStatus.SENDING
  );

  const resendDisabled = computed(
    () => otpData.resendOtpTimeout > 0
  );

  return { otpData, sendOtp, resetOtp, sendingOtp, resendDisabled };
}

/**
 * Validate OTP for scheme plan application.
 */
export function useValidateApplicationOtp() {
  const validateStatus = ref(requestStatus.NOT_SENDING);
  const validating = computed(
    () => validateStatus.value === requestStatus.SENDING
  );
  const otpInvalid = computed(() => validateStatus.value === INVALID_OTP);

  async function validateOtp({ idNumber, otp }) {
    validateStatus.value = requestStatus.SENDING;
    try {
      await apiCall({
        url: `${BASE_URL}/validate-otp`,
        data: { id_number: idNumber, otp },
        method: "POST",
      });
      validateStatus.value = requestStatus.COMPLETE;
    } catch (error) {
      validateStatus.value = requestStatus.ERROR;
      const responseError = error?.response;
      const errorCode = responseError?.status;
      const errorText = responseError?.data?.message;
      if (isOtpInvalid({ errorCode, errorText })) {
        validateStatus.value = INVALID_OTP;
      }
      throw error;
    }
  }

  function resetOtpStatus() {
    validateStatus.value = requestStatus.NOT_SENDING;
  }

  return { validating, otpInvalid, validateOtp, resetOtpStatus };
}

/**
 * Fetch form lookup data (categories, types, supporting documents).
 */
export function useFetchApplicationFormData() {
  const formData = ref(null);
  const loading = ref(false);

  async function fetchFormData() {
    loading.value = true;
    try {
      const response = await apiCall({
        url: `${BASE_URL}/form-data`,
        method: "GET",
      });
      formData.value = response;
      return response;
    } catch (e) {
      console.error("Failed to load application form data", e);
      return null;
    } finally {
      loading.value = false;
    }
  }

  return { formData, loading, fetchFormData };
}

/**
 * Submit a scheme plan application with file uploads.
 */
export function useSubmitApplication() {
  const submitStatus = ref(requestStatus.NOT_SENDING);
  const submitting = computed(
    () => submitStatus.value === requestStatus.SENDING
  );
  const submitError = ref(null);

  async function submitApplication(formDataObj) {
    submitStatus.value = requestStatus.SENDING;
    submitError.value = null;
    try {
      const response = await apiCall({
        url: `${BASE_URL}`,
        data: formDataObj,
        method: "POST",
      });
      submitStatus.value = requestStatus.COMPLETE;
      return response;
    } catch (error) {
      submitStatus.value = requestStatus.ERROR;
      submitError.value =
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        "Failed to submit application.";
      throw error;
    }
  }

  return { submitting, submitError, submitApplication };
}

/**
 * Create or return an existing draft application for the given property.
 */
export function useCreateDraft() {
  const creating = ref(false);
  const createError = ref(null);

  async function createDraft({ propertyId }) {
    creating.value = true;
    createError.value = null;
    try {
      const response = await apiCall({
        url: `${BASE_URL}/draft`,
        data: { scheme_plan_property_id: propertyId },
        method: "POST",
      });
      return response;
    } catch (error) {
      createError.value =
        error?.response?.data?.message || "Failed to start draft application.";
      throw error;
    } finally {
      creating.value = false;
    }
  }

  return { creating, createError, createDraft };
}

/**
 * Save draft form fields (category, type, remarks, application_data).
 */
export function useUpdateDraft() {
  async function updateDraft(draftId, fields) {
    try {
      await apiCall({
        url: `${BASE_URL}/${draftId}/draft`,
        data: fields,
        method: "PUT",
      });
    } catch (_) {
      // silently ignore auto-save errors
    }
  }

  return { updateDraft };
}

/**
 * Upload a single supporting document to a draft application.
 */
export function useUploadDraftDocument() {
  async function uploadDocument(draftId, supportingDocumentId, file) {
    const fd = new FormData();
    fd.append("supporting_document_id", supportingDocumentId);
    fd.append("file", file);
    const response = await apiCall({
      url: `${BASE_URL}/${draftId}/document`,
      data: fd,
      method: "POST",
    });
    return response;
  }

  return { uploadDocument };
}

/**
 * Remove an uploaded document from a draft application.
 */
export function useRemoveDraftDocument() {
  async function removeDocument(draftId, documentId) {
    await apiCall({
      url: `${BASE_URL}/${draftId}/document/${documentId}`,
      method: "DELETE",
    });
  }

  return { removeDocument };
}

/**
 * Submit (finalize) a draft application.
 */
export function useSubmitDraft() {
  const submitting = ref(false);
  const submitError = ref(null);

  async function submitDraft(draftId, formDataObj) {
    submitting.value = true;
    submitError.value = null;
    try {
      const response = await apiCall({
        url: `${BASE_URL}/${draftId}/submit`,
        data: formDataObj,
        method: "POST",
      });
      return response;
    } catch (error) {
      submitError.value =
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        "Failed to submit application.";
      throw error;
    } finally {
      submitting.value = false;
    }
  }

  return { submitting, submitError, submitDraft };
}

export const preloadedDraft = ref(null);

/**
 * Load a draft application by ID.
 */
export function useLoadDraft() {
  const loading = ref(false);
  const loadError = ref(null);

  async function loadDraft(draftId) {
    loading.value = true;
    loadError.value = null;
    try {
      const response = await apiCall({
        url: `${BASE_URL}/${draftId}`,
        method: "GET",
      });
      return response;
    } catch (error) {
      loadError.value = error?.response?.data?.message || "Failed to load draft.";
      throw error;
    } finally {
      loading.value = false;
    }
  }

  return { loading, loadError, loadDraft };
}

/**
 * Fetch land use plans filtered by adjudication name(s).
 */
export function useFetchLandUsePlans() {
  const landUsePlans = ref([]);
  const loading = ref(false);

  async function fetchLandUsePlans(adjudicationNames) {
    if (!adjudicationNames || adjudicationNames.length === 0) {
      landUsePlans.value = [];
      return;
    }
    loading.value = true;
    try {
      const params = new URLSearchParams();
      adjudicationNames.forEach((name) => params.append("adjudication_names[]", name));
      const response = await apiCall({
        url: `${BASE_URL}/land-use-plans?${params.toString()}`,
        method: "GET",
      });
      landUsePlans.value = response;
    } catch (e) {
      console.error("Failed to fetch land use plans", e);
      landUsePlans.value = [];
    } finally {
      loading.value = false;
    }
  }

  return { landUsePlans, loading, fetchLandUsePlans };
}

/**
 * Fetch scheme plan applications for the authenticated client.
 * Supports debounced search and pagination.
 */
export function useFetchSchemePlanApplications() {
  const searchModel = ref("");
  const debouncedSearchModel = ref(null);

  const { paginationData: applicationData } = usePaginatedData();

  const url = computed(() => {
    let u = `${BASE_URL}?page=${applicationData.paginationData.currentPage}`;

    if (stringNotEmpty(debouncedSearchModel.value)) {
      u += `&search=${debouncedSearchModel.value}`;
    }

    return u;
  });

  const setDebouncedSearch = debounce((searchValue) => {
    applicationData.setNewPage(1);
    debouncedSearchModel.value = searchValue;
  }, searchDebounceTimeInMs);

  watch(searchModel, (newVal) => {
    setDebouncedSearch(newVal);
  });

  watch(
    url,
    async (newUrl) => {
      applicationData.fetchData(newUrl);
    },
    { immediate: true }
  );

  function fetchApplications() {
    applicationData.fetchData(url.value);
  }

  return {
    applicationData,
    searchModel,
    fetchApplications,
  };
}

/**
 * Fetch scheme plan applications for properties owned by the authenticated client.
 * (property.client_id = auth user id)
 */
export function useFetchMyPropertySchemePlanApplications() {
  const searchModel = ref("");
  const debouncedSearchModel = ref(null);

  const { paginationData: applicationData } = usePaginatedData();

  const url = computed(() => {
    let u = `${BASE_URL}/my-property-applications?page=${applicationData.paginationData.currentPage}`;

    if (stringNotEmpty(debouncedSearchModel.value)) {
      u += `&search=${debouncedSearchModel.value}`;
    }

    return u;
  });

  const setDebouncedSearch = debounce((searchValue) => {
    applicationData.setNewPage(1);
    debouncedSearchModel.value = searchValue;
  }, searchDebounceTimeInMs);

  watch(searchModel, (newVal) => {
    setDebouncedSearch(newVal);
  });

  watch(
    url,
    async (newUrl) => {
      applicationData.fetchData(newUrl);
    },
    { immediate: true }
  );

  function fetchApplications() {
    applicationData.fetchData(url.value);
  }

  return {
    applicationData,
    searchModel,
    fetchApplications,
  };
}

/**
 * Fetch scheme plan applications created by the authenticated user.
 * (scheme_plan_applications.created_by = auth user id)
 */
export function useFetchClientCreatedSchemePlanApplications() {
  const searchModel = ref("");
  const debouncedSearchModel = ref(null);

  const { paginationData: applicationData } = usePaginatedData();

  const url = computed(() => {
    let u = `${BASE_URL}/created-by-me?page=${applicationData.paginationData.currentPage}`;

    if (stringNotEmpty(debouncedSearchModel.value)) {
      u += `&search=${debouncedSearchModel.value}`;
    }

    return u;
  });

  const setDebouncedSearch = debounce((searchValue) => {
    applicationData.setNewPage(1);
    debouncedSearchModel.value = searchValue;
  }, searchDebounceTimeInMs);

  watch(searchModel, (newVal) => {
    setDebouncedSearch(newVal);
  });

  watch(
    url,
    async (newUrl) => {
      applicationData.fetchData(newUrl);
    },
    { immediate: true }
  );

  function fetchApplications() {
    applicationData.fetchData(url.value);
  }

  return {
    applicationData,
    searchModel,
    fetchApplications,
  };
}

/**
 * Fetch scheme plan properties available for amalgamation.
 * Returns ACT properties belonging to the same client, excluding the mother property.
 */
export function useFetchAmalgamationProperties() {
  const amalgamLoading = ref(false);
  const amalgamProperties = ref([]);

  async function fetchAmalgamationProperties({ idNumber, excludeId }) {
    if (!idNumber) return;
    amalgamLoading.value = true;
    try {
      const response = await apiCall({
        url: `${BASE_URL}/amalgamation-properties?id_number=${encodeURIComponent(idNumber)}&exclude_id=${excludeId}`,
        method: "GET",
      });
      amalgamProperties.value = Array.isArray(response) ? response : [];
    } catch (e) {
      amalgamProperties.value = [];
    } finally {
      amalgamLoading.value = false;
    }
  }

  return { amalgamLoading, amalgamProperties, fetchAmalgamationProperties };
}
